import { Button, Chip, Icon, IconButton, makeStyles, Paper, TextField, Theme } from '@material-ui/core';
import { capitalizeAll, capitalizeFirst, ModeSelectInputField, SelectInputOption, useModeFormStyle } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import { useCallback, useMemo, useState, useContext, useEffect, useRef } from 'react';
import { selectEntityById, setEntity, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { AppAPI, AlertTargetMetric, EntityTarget, AlertConditionType, SmartModuleType } from '@moderepo/mode-apis';
import { AlertRuleEditorStep, useEditAlertRuleStyle } from '.';
import { SelectEntityDropdownMenuComp } from '../../../components';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectProject } from '../../../utils/dataStateHooks';
import { ReadonlyNonEmptyArray } from '../../../utils/utils';
import { EntitiesListingContainerProps, useSelectTranslatedEntityClasses } from '../..';


const useStyle = makeStyles((theme: Theme) => {
    return {
        targetSetupForm: {
            display      : 'flex',
            flexDirection: 'column',
            position     : 'relative',
        },

        targetSetupFormGroup: {
            width        : '100%',
            flex         : 1,
            display      : 'flex',
            flexDirection: 'row',
            flexWrap     : 'wrap',

            '&:not(:last-child)': {
                marginBottom: theme.spacing(2),
            },

            '&.readonly': {
                pointerEvents: 'none',
            },

            '& .form-control': {
                width: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection: 'row',
                flexWrap     : 'nowrap',

                '& .form-control': {
                    width                           : 'auto',
                    '&:last-child:not(:first-child)': {
                    },
                    '&:not(:last-child)': {
                        marginRight : theme.spacing(1),
                        marginBottom: 0,
                    },
                },
            },
        },

        targetTypeInput: {
            minWidth: '10em',
        },

        entityIdsInputField: {
            flex    : 1,
            overflow: 'hidden',
        },

        entityIdsInputFieldInput: {
            overflow : 'auto',
            flexWrap : 'wrap',
            '& input': {
                display: 'inline-block',
                width  : 1,
            },
        },

        entityIdsChipContainer: {
            display       : 'flex',
            justifyContent: 'center',
            flexWrap      : 'wrap',
        },

        entityIdChip: {
            margin: 2,
        },

        addEntityButton: {
            marginTop: theme.spacing(2),
            display  : 'inline-block',
            height   : 'fit-content',
        },
    };
});


enum ExcludeAndTargetBy {
    INCLUDE_ALL = 0,                // excluded=undefined, targetBy=undefined
    INCLUDE_CHILD_ENTITIES = 1,     // exclude=false, targetBy=parents
    INCLUDE_ENTITIES = 2,           // exclude=false, targetBy=entities
    EXCLUDE_CHILD_ENTITIES = 3,     // exclude=true, targetBy=parents
    EXCLUDE_ENTITIES = 4,           // exclude=true, targetBy=entities
}


/**
 * Combine and convert exclude and targetBy values to ExcludeAndTargetBy enum value
 */
const excludeAndTargetByToEnum = (exclude: boolean | undefined, targetBy: EntityTarget | undefined): ExcludeAndTargetBy => {
    if (exclude === false && targetBy === EntityTarget.PARENTS) {
        return ExcludeAndTargetBy.INCLUDE_CHILD_ENTITIES;
    }
    if (exclude === false && targetBy === EntityTarget.ENTITIES) {
        return ExcludeAndTargetBy.INCLUDE_ENTITIES;
    }
    if (exclude === true && targetBy === EntityTarget.PARENTS) {
        return ExcludeAndTargetBy.EXCLUDE_CHILD_ENTITIES;
    }
    if (exclude === true && targetBy === EntityTarget.ENTITIES) {
        return ExcludeAndTargetBy.EXCLUDE_ENTITIES;
    }
    return ExcludeAndTargetBy.INCLUDE_ALL;
};


/**
 * Convert ExcludeAndTargetBy enum value back to exclude and targetBy values. The return value will be an object with 2 attributes,
 * "exclude" and "targetBy". Return "undefined" if the value is undefined or equal ExcludeAndTargetBy.INCLUDE_ALL
 */
const excludeAndTargetByEnumToValues = (enumValue: ExcludeAndTargetBy | undefined): {exclude: boolean, targetBy: EntityTarget} | undefined => {
    if (enumValue === undefined) {
        return undefined;
    }

    switch (enumValue) {
        case ExcludeAndTargetBy.INCLUDE_CHILD_ENTITIES:
            return { exclude: false, targetBy: EntityTarget.PARENTS };
        case ExcludeAndTargetBy.INCLUDE_ENTITIES:
            return { exclude: false, targetBy: EntityTarget.ENTITIES };
        case ExcludeAndTargetBy.EXCLUDE_CHILD_ENTITIES:
            return { exclude: true, targetBy: EntityTarget.PARENTS };
        case ExcludeAndTargetBy.EXCLUDE_ENTITIES:
            return { exclude: true, targetBy: EntityTarget.ENTITIES };
        default:
            return undefined;
    }
};


/**
 * The structure for the Target Metric Step errors
 */
interface StepTargetInputErrors {
    entityClass?: string | undefined;
    metricsDefinitionId?: string | undefined;
    metricName?: string | undefined;
    excludeAndTargetBy?: string | undefined;
    entityIds?: string | undefined;
}


interface AlertRuleTargetInputs {
    readonly entityClass?: string | undefined;
    readonly metricsDefinitionId?: string | undefined;
    readonly metricName?: string | undefined;
    readonly excludeAndTargetBy?: ExcludeAndTargetBy | undefined;
    readonly entityIds?: readonly string[] | undefined;
}


const convertAlertTargetMetricToInputs = (
    alertTargetMetric: AlertTargetMetric | undefined,
): AlertRuleTargetInputs => {

    if (alertTargetMetric) {
        return {
            entityClass        : alertTargetMetric.entityClass,
            metricsDefinitionId: alertTargetMetric.metricsDefinitionId,
            metricName         : alertTargetMetric.metricName,
            excludeAndTargetBy : excludeAndTargetByToEnum(
                alertTargetMetric.entityTargetCondition?.exclude, alertTargetMetric.entityTargetCondition?.targetBy,
            ),
            entityIds: alertTargetMetric.entityTargetCondition?.entityIds,
        };
    }

    // Return a new
    return { };
};



/**
 * The required props for the EditAlertRuleStepInfo in addition to the props inherited from AlertRuleEditorStep
 */
export interface EditAlertRuleStepTargetMetricProps extends AlertRuleEditorStep {
    readonly alertRuleTargetMetric?: AlertTargetMetric | undefined;
    readonly onStepCompleted: (targetMetric: AlertTargetMetric)=> void;
}


/**
 * This is the Edit Alert Rule Info step which is the step which the user needs to provide the basic info for the alert rule e.g.
 * name, description, etc...
 */
export const EditAlertRuleStepTargetMetric: React.FC<EditAlertRuleStepTargetMetricProps> = (props: EditAlertRuleStepTargetMetricProps) => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const sharedClasses = useEditAlertRuleStyle();
    const classes = useStyle();
    const formClasses = useModeFormStyle();

    // The state to keep track of the inputs for all the targets
    const [targetInputs, setTargetInputs] = useState<AlertRuleTargetInputs>(
        convertAlertTargetMetricToInputs(props.alertRuleTargetMetric),
    );

    // The state to keep track of the input errors for all the target inputs
    const [inputErrors, setInputErrors] = useState<StepTargetInputErrors | undefined>();

    // A reference to the + entity button
    const addEntityButtonRef = useRef<HTMLElement>();

    /**
     * If there are existing "alertRuleTargetMetric" to initialize this component with, we will need to make sure we have Entity objects
     * for all the entityIds specified in the "alertRuleTargetMetric". However, we only need to do this once when the component initialize.
     * Therefore, this useEffect will be used for fetching all the entity objects in the "alertRuleTargetMetric"'s entityIds arrays that we
     * have not fetched yet.
     *
     * NOTE: because we only need to do this once, we will use the "entitiesFetched" flag to remember whether or not we have tried to fetch
     * entity objects.
     */
    const entitiesFetched = useRef<boolean>(false);
    useEffect(() => {
        // If there ate existing "alertRuleTargetMetric" but we have not try to fetch entity objects yet then do it.
        if (!entitiesFetched.current && props.alertRuleTargetMetric) {

            // Mark entitiesFetched as true so that we don't do this again
            entitiesFetched.current = true;

            // First, compile a list of UNIQUE entityIds from all the alertRuleTargetMetric
            const entityIdsToBeFetch = new Set<string>();
            props.alertRuleTargetMetric.entityTargetCondition?.entityIds.forEach((entityId) => {
                // Check the data state to see if we already have Entity object for the entityId. If we don't, then add the entityId to
                // the list of entityIds to be fetched
                const entity = selectEntityById(appDataState, props.projectId, entityId);
                if (!entity) {
                    entityIdsToBeFetch.add(entityId);
                }
            });

            // Now make API call to fetch the entity and then add the result to the data state
            entityIdsToBeFetch.forEach((entityId) => {
                AppAPI.getInstance().getEntityById(props.projectId, entityId).then((entity) => {
                    appDataDispatch(setEntity(props.projectId, entity));
                }).catch(() => {
                    // Entity probably does not exist so ignore this entityId
                });
            });
        }
    }, [appDataDispatch, appDataState, props.alertRuleTargetMetric, props.projectId]);



    /**
     * Create and cache the list of entity class options to be passed to the SELECT entity class input
     */
    const entityClassOptions = useMemo((): readonly SelectInputOption<string>[] => {
        if (entityClasses) {
            return entityClasses.map((entityClass) => {
                return {
                    label: entityClass.displaySettings.name,
                    value: entityClass.entityClass,
                };
            });
        }

        return [];
    }, [entityClasses]);


    /**
     * Create and cache the list of metric definition options to be passed to the SELECT metric definition input.
     * NOTE: This will depends on the selected entityClass for each target. So if there are 2 targets for each different entity classes,
     * there will be 2 arrays of metricDefinitionOptions.
     */
    const metricsDefinitionOptions = useMemo((): readonly SelectInputOption<string>[] => {
        if (entityClasses) {
            if (targetInputs.entityClass) {
                // Find the entityClass object based on the target.entityClass
                const entityClass = entityClasses.find((entityClass) => {
                    return entityClass.entityClass === targetInputs.entityClass;
                });

                // If entityClass object found, create an array of metricDefinition options based on the entityClass's metricsDefinitions
                // Note, we will only allow TSDB metrics definitions.
                if (entityClass) {
                    return entityClass.metricsDefinitions?.filter((metricsDef) => {
                        return metricsDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB;
                    })?.map((metricDef) => {
                        return {
                            label: metricDef.displayName || metricDef.metricsDefinitionId,
                            value: metricDef.metricsDefinitionId,
                        };
                    }) ?? [];
                }
            }
        }

        return [];
    }, [targetInputs, entityClasses]);


    /**
     * Create and cache the list of metric name options to be passed to the SELECT metric name input.
     */
    const metricNameOptions = useMemo((): readonly SelectInputOption<string>[] => {

        if (entityClasses) {
            if (targetInputs.entityClass) {
                // Find the entityClass and metricDefinition object based on the target.entityClass and target.metricsDefinitionId
                const entityClass = entityClasses.find((entityClass) => {
                    return entityClass.entityClass === targetInputs.entityClass;
                });

                const metricDef = entityClass?.metricsDefinitions?.find((metricDef) => {
                    return metricDef.metricsDefinitionId === targetInputs.metricsDefinitionId;
                });

                // If metricDefinition object found, create an array of metricName options based on the metricDefinitions's numericMetrics
                if (metricDef) {
                    return [
                        // Include the "All" option ONLY if the conditionType is HEARTBEAT
                        ...(props.alertConditionType === AlertConditionType.METRIC_HEARTBEAT
                            ? [{
                                label: capitalizeAll(trans('label.all')),
                                value: '',
                            }] : []),

                        // Add each numeric metric to the list
                        ...(metricDef.numericMetrics?.map((metric) => {
                            return {
                                label: metric.evaluator?.displayName ?? metric.displayName ?? metric.name,
                                value: metric.name,
                            };
                        }) ?? []),
                    ];
                }
            }
            return [];
        }

        return [];
    }, [entityClasses, targetInputs, props.alertConditionType, trans]);


    /**
     * Create and cache the Exclude/Include and Target By options
     */
    const excludeAndTargetByOptions = useMemo((): readonly SelectInputOption<ExcludeAndTargetBy>[] => {
        return [{
            label: capitalizeFirst(trans('alerting.edit_alert_rule.exclude_and_target_by_option.no_condition')),
            value: ExcludeAndTargetBy.INCLUDE_ALL,
        }, {
            label: capitalizeFirst(trans('alerting.edit_alert_rule.exclude_and_target_by_option.include_child_entities')),
            value: ExcludeAndTargetBy.INCLUDE_CHILD_ENTITIES,
        }, {
            label: capitalizeFirst(trans('alerting.edit_alert_rule.exclude_and_target_by_option.include_entities')),
            value: ExcludeAndTargetBy.INCLUDE_ENTITIES,
        }, {
            label: capitalizeFirst(trans('alerting.edit_alert_rule.exclude_and_target_by_option.exclude_child_entities')),
            value: ExcludeAndTargetBy.EXCLUDE_CHILD_ENTITIES,
        }, {
            label: capitalizeFirst(trans('alerting.edit_alert_rule.exclude_and_target_by_option.exclude_entities')),
            value: ExcludeAndTargetBy.EXCLUDE_ENTITIES,
        }];
    }, [trans]);



    /**
     * Submit the user inputs for this step. Before submitting the inputs, we will validate the inputs. If there are no errors,
     * we will notify the parent container by calling ths 'onStepCompleted' callback and provide the parent container with the new
     * inputs and return 'true' for success. If there are errors, we will notify the parent container that there are errors but only
     * if 'dispatchOnError' is true and also return false for failure.
     */
    const submitInputs = useCallback((dispatchOnError: boolean = true): boolean => {

        const newInputErrors: StepTargetInputErrors = {};


        // Find the entityClass, metricsDefinition, and numericMetric based on the targetMetricInputs
        const entityClass = entityClasses?.find((entityClass) => {
            return entityClass.entityClass === targetInputs.entityClass;
        });
        const metricDef = entityClass?.metricsDefinitions?.find((metricDef) => {
            return metricDef.metricsDefinitionId === targetInputs.metricsDefinitionId;
        });
        const numericMetric = metricDef?.numericMetrics?.find((metric) => {
            return metric.name === targetInputs.metricName;
        });

        if (!targetInputs.entityClass) {
            newInputErrors.entityClass = 'error.input.generic.empty';
        } else if (!entityClass) {
            // Make sure entityClass exist.
            newInputErrors.entityClass = 'error.input.generic.invalid';
        }

        if (!targetInputs.metricsDefinitionId) {
            newInputErrors.metricsDefinitionId = 'error.input.generic.empty';
        } else if (!metricDef) {
            // Make sure "metricsDefinitionId" exist in the entity class
            newInputErrors.metricsDefinitionId = 'error.input.generic.invalid';
        }

        // metricName is OPTIONAL for alert condition type HEARTBEAT but is required for type THRESHOLD.
        const metricNameRequired = props.alertConditionType === AlertConditionType.METRIC_THRESHOLD;
        if (metricNameRequired && !targetInputs.metricName) {
            newInputErrors.metricName = 'error.input.generic.empty';
        }

        // If metricName is required or if it is not required but a value is provided then the metricName must be a valid value.
        // The metric name must be one of the existing numeric metrics
        if ((metricNameRequired || targetInputs.metricName) && !numericMetric) {
            newInputErrors.metricName = 'error.input.generic.invalid';
        }

        // "excludeAndTargetBy" is OPTIONAL but if it is specified AND if the selected value is NOT ExcludeAndTargetBy.INCLUDE_ALL
        // then we need to make sure entityIds is selected
        if (targetInputs.excludeAndTargetBy !== undefined && targetInputs.excludeAndTargetBy !== ExcludeAndTargetBy.INCLUDE_ALL) {
            if (!targetInputs.entityIds || targetInputs.entityIds.length === 0) {
                newInputErrors.entityIds = 'error.input.generic.empty';
            }
        }


        // Update the local inputErrors
        setInputErrors(newInputErrors);


        // If target is empty or there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });


        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        // If there are no errors, notify the parent container this step is completed and pass the parent the input data
        if (!hasErrors) {

            // Convert targetsInputs to array of AlertTargetMetric. AlertTargetMetric is the structure we need to send to backend.
            const excludeAndTargetByInputs = excludeAndTargetByEnumToValues(targetInputs.excludeAndTargetBy);
            const target: AlertTargetMetric = {
                entityClass          : targetInputs.entityClass as string,
                metricsDefinitionId  : targetInputs.metricsDefinitionId as string,
                metricName           : targetInputs.metricName ? targetInputs.metricName : undefined,
                entityTargetCondition: excludeAndTargetByInputs ? {
                    ...excludeAndTargetByInputs,
                    entityIds: targetInputs.entityIds as ReadonlyNonEmptyArray<string>,
                } : undefined,
            };

            props.onStepCompleted(target);
        }

        return !hasErrors;
    }, [entityClasses, props, targetInputs]);


    // check if the user selected the excludeAndTargetBy option that target specific entities
    const isTargetSpecificEntities = targetInputs.excludeAndTargetBy === ExcludeAndTargetBy.EXCLUDE_ENTITIES
                                                        || targetInputs.excludeAndTargetBy === ExcludeAndTargetBy.INCLUDE_ENTITIES;

    // If targeting specific entities then when we show the list of entities for the user to select, we will only show the
    // entities of the selected entity class.
    const presetFilters: EntitiesListingContainerProps['presetFilters'] = isTargetSpecificEntities && targetInputs.entityClass ? {
        entityClasses: [targetInputs.entityClass],
    } : undefined;

    return (
        <div className={sharedClasses.stepContentBody}>
            <div className={clsx(sharedClasses.stepContentForm)}>
                <div className={clsx(sharedClasses.stepSubtitle)}>
                    {trans('alerting.edit_alert_rule.step_target_metrics.subtitle')}
                </div>


                <Paper
                    elevation={1}
                    className={clsx(sharedClasses.formControlGroup, classes.targetSetupForm)}
                >
                    <div className={classes.targetSetupFormGroup}>
                        <ModeSelectInputField
                            className={clsx(formClasses.formInputField, sharedClasses.formControl, classes.targetTypeInput, 'form-control')}
                            fieldName="entityClass"
                            label={capitalizeAll(trans('alerting.edit_alert_rule.label.target_entity'))}
                            required
                            editable
                            isInEditMode
                            hideEditableIcon
                            value={targetInputs.entityClass ?? ''}
                            options={entityClassOptions}
                            error={inputErrors?.entityClass ? trans(inputErrors?.entityClass ?? '') : undefined}
                            onInputChange={(fieldName, newValue) => {
                                // Input changed so clear the error
                                props.onStepError(false);
                                setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                    delete draft?.entityClass;
                                    delete draft?.metricsDefinitionId;
                                    delete draft?.metricName;
                                    delete draft?.excludeAndTargetBy;
                                    delete draft?.entityIds;
                                }));

                                // update the state
                                setTargetInputs((currentTarget) => {

                                    // update the target at index
                                    const newTargets = {
                                        ...currentTarget,
                                        entityClass        : newValue,
                                        // Because the user selected a different entityClass, the target's current metricsDefinitionId,
                                        // metricName, exclude, targetBy, entityId, should not be used anymore since the new entity
                                        // class will probably have different set of metrics definitions and different metric names, etc..
                                        // Therefore, we must clear everything else
                                        metricsDefinitionId: undefined,
                                        metricName         : undefined,
                                        excludeAndTargetBy : undefined,
                                        entityIds          : undefined,
                                    };

                                    // return the new targets
                                    return newTargets;
                                });
                            }}
                        />

                        {/* Need to wait until entityClass is selected before we can show the 'metricsDefinitionId' input */}
                        {targetInputs.entityClass && (
                            <ModeSelectInputField
                                className={clsx(
                                    formClasses.formInputField, sharedClasses.formControl, classes.targetTypeInput, 'form-control',
                                )}
                                label={capitalizeAll(trans('entity.label.metrics_definition'))}
                                fieldName="metricsDefinitionId"
                                required
                                editable
                                isInEditMode
                                hideEditableIcon
                                value={targetInputs.metricsDefinitionId ?? ''}
                                options={metricsDefinitionOptions ?? []}
                                error={inputErrors?.metricsDefinitionId
                                    ? trans(inputErrors?.metricsDefinitionId ?? '') : undefined}
                                onInputChange={(fieldName, newValue) => {
                                    // Input changed so clear the error
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                        delete draft?.metricsDefinitionId;
                                        delete draft?.metricName;
                                        delete draft?.excludeAndTargetBy;
                                    }));

                                    // update the state
                                    setTargetInputs((currentTarget) => {
                                        // update the target at index
                                        const newTargets = {
                                            ...currentTarget,
                                            metricsDefinitionId: newValue,
                                            metricName         : undefined,
                                            excludeAndTargetBy : undefined,
                                            entityIds          : undefined,
                                        };

                                        // return the new targets
                                        return newTargets;
                                    });
                                }}
                            />
                        )}

                        {/*
                            Need to wait until entityClass and metricsDefinitionId are selected before we can show the
                            'metricsDefinitionId' input
                        */}
                        {targetInputs.entityClass && targetInputs.metricsDefinitionId && (
                            <ModeSelectInputField
                                className={clsx(
                                    formClasses.formInputField, sharedClasses.formControl, classes.targetTypeInput, 'form-control',
                                )}
                                label={capitalizeAll(trans('label.metric'))}
                                fieldName="metricName"
                                editable
                                isInEditMode
                                hideEditableIcon
                                value={targetInputs.metricName ?? ''}
                                options={metricNameOptions ?? []}
                                error={inputErrors?.metricName ? trans(inputErrors?.metricName ?? '') : undefined}
                                onInputChange={(fieldName, newValue) => {
                                    // Input changed so clear the error
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                        delete draft?.metricName;
                                    }));

                                    // update the state
                                    setTargetInputs((currentTarget) => {
                                        // update the target at index
                                        const newTargets = {
                                            ...currentTarget,
                                            metricName: newValue,
                                        };

                                        // return the new targets
                                        return newTargets;
                                    });
                                }}
                            />
                        )}
                    </div>

                    {/*
                        Need to wait until entityClass and metricsDefinitionId are selected before we can show the
                        conditions input
                    */}
                    {targetInputs.entityClass && targetInputs.metricsDefinitionId && (
                        <div className={classes.targetSetupFormGroup}>
                            <ModeSelectInputField
                                className={clsx(
                                    formClasses.formInputField, sharedClasses.formControl, classes.targetTypeInput, 'form-control',
                                )}
                                label={capitalizeAll(trans('alerting.edit_alert_rule.label.exclude_condition'))}
                                fieldName="exclude"
                                editable
                                isInEditMode
                                hideEditableIcon
                                value={targetInputs?.excludeAndTargetBy ?? ExcludeAndTargetBy.INCLUDE_ALL}
                                options={excludeAndTargetByOptions ?? []}
                                onInputChange={(fieldName, newValue) => {
                                    // Input changed so clear the error
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                        delete draft?.excludeAndTargetBy;
                                    }));

                                    // update the state
                                    setTargetInputs((currentTarget) => {
                                        // update the target at index
                                        const newTargets = {
                                            ...currentTarget,
                                            excludeAndTargetBy: newValue,
                                            // When the user changed the 'excludeAndTargetBy', we need to clear the selected entities
                                            // because the selected entities might not be valid anymore
                                            entityIds         : undefined,
                                        };

                                        // return the new targets
                                        return newTargets;
                                    });
                                }}
                            />

                            {/*
                                Only show entityIds option if the user already chose the excludeAndTargetBy option and the selected option
                                is NOT ExcludeAndTargetBy.INCLUDE_ALL
                            */}
                            {targetInputs.excludeAndTargetBy !== undefined && targetInputs.excludeAndTargetBy !== ExcludeAndTargetBy.INCLUDE_ALL && (
                                <>
                                    <TextField
                                        className={clsx(
                                            sharedClasses.formControl, classes.targetTypeInput, classes.entityIdsInputField, 'form-control',
                                        )}
                                        label={capitalizeAll(trans('terms.entity_other'))}
                                        variant="standard"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        required
                                        InputProps={{
                                            readOnly      : true,
                                            className     : classes.entityIdsInputFieldInput,
                                            startAdornment: targetInputs.entityIds?.map((entityId) => {
                                                return (
                                                    <Chip
                                                        key={entityId}
                                                        className={classes.entityIdChip}
                                                        variant="default"
                                                        size="small"
                                                        label={
                                                            // Find the entity by entityId and use the entity's name is possible
                                                            selectEntityById(
                                                                appDataState, props.projectId, entityId,
                                                            )?.displaySettings.name ?? entityId
                                                        }
                                                        onDelete={() => {

                                                            // Delete the entity at index and update the state
                                                            setTargetInputs((currentTarget) => {
                                                                // update the target at index
                                                                const newTargets = {
                                                                    ...currentTarget,
                                                                    entityIds: (targetInputs.entityIds ?? []).filter((id) => {
                                                                        return id !== entityId;
                                                                    }),
                                                                };

                                                                // return the new targets
                                                                return newTargets;
                                                            });
                                                        }}
                                                    />
                                                );
                                            }),
                                        }}
                                        error={Boolean(inputErrors?.entityIds)}
                                        helperText={inputErrors?.entityIds ? trans(inputErrors?.entityIds ?? '') : undefined}
                                        onClick={() => {
                                            if (addEntityButtonRef.current) {
                                                addEntityButtonRef.current.click();
                                            }
                                        }}
                                    />
                                    {/**
                                     * Add Entity dropdown select control to allow the user to add entity
                                     */}
                                    <SelectEntityDropdownMenuComp
                                        key={targetInputs.excludeAndTargetBy}
                                        className={classes.addEntityButton}
                                        togglerComponent={(
                                            <IconButton innerRef={addEntityButtonRef} size="small">
                                                <Icon fontSize="small">add</Icon>
                                            </IconButton>
                                        )}
                                        entitiesListingContainerProps={{
                                            projectId           : props.projectId,
                                            minimalUI           : true,
                                            disablePreviewOption: false,
                                            presetFilters,
                                            // If there is entityClasses filter then we can disable the tree expansion because the top
                                            // level of the tree will show all the entities of the selected class. There is no need to
                                            // drill down the tree to find children entities of the selected class.
                                            disableTreeExpansion: presetFilters !== undefined,
                                        }}
                                        texts={{
                                            clearButtonLabel : capitalizeAll(trans('entity.action.make_as_root')),
                                            cancelButtonLabel: capitalizeAll(trans('label.cancel')),
                                            selectButtonLabel: capitalizeAll(trans('label.select')),
                                            emptyValueDisplay: capitalizeAll(trans('entity.label.root')),
                                        }}
                                        onEntitySelected={(entity) => {
                                            // Input changed so clear the error and update the state
                                            props.onStepError(false);
                                            setInputErrors(produce(inputErrors, (draft: Draft<typeof inputErrors>) => {
                                                delete draft?.entityIds;
                                            }));


                                            // Make sure the selected entity does not already exist in the entityIds array
                                            // If it is already existed, don't need to add it to the entityIds array
                                            if (targetInputs.entityIds?.includes(entity.entityId)) {
                                                return;
                                            }

                                            // Add the selected entity to the entityIds array and update the state
                                            setTargetInputs((currentTarget) => {
                                                // update the target at index
                                                const newTargets = {
                                                    ...currentTarget,
                                                    entityIds: [
                                                        ...(currentTarget.entityIds ?? []),
                                                        entity.entityId,
                                                    ],
                                                };

                                                // return the new targets
                                                return newTargets;
                                            });
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </Paper>
            </div>

            <div className={sharedClasses.stepContentActionsContainer}>
                <div className={sharedClasses.stepContentLeftActionsContainer} />
                <div className={sharedClasses.stepContentRightActionsContainer}>
                    <Button
                        disabled={!props.canGoBack}
                        onClick={() => {
                            // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                            // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will
                            // not save the user's input since it is invalid anyway.
                            props.onStepError(false);
                            submitInputs(false);
                            props.onPrevStepClick();
                        }}
                        className={sharedClasses.navButton}
                        startIcon={<Icon>navigate_before</Icon>}
                    >
                        {capitalizeAll(trans('label.back'))}
                    </Button>
                    <Button
                        onClick={() => {
                            if (submitInputs()) {
                                props.onNextStepClick();
                            }
                        }}
                        className={sharedClasses.navButton}
                        endIcon={<Icon>navigate_next</Icon>}
                    >
                        {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                    </Button>
                    {!props.isNewAlertRule && (
                        <Button
                            variant="contained"
                            color="default"
                            size="small"
                            onClick={() => {
                                if (props.onGotoLastStepClick && submitInputs()) {
                                    props.onGotoLastStepClick();
                                }
                            }}
                            className={clsx(sharedClasses.navButton, 'finish')}
                        >
                            {capitalizeAll(trans('label.finish'))}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
